import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { APP_VERSION_LABEL, LIST_PERMISSIONS } from '@pmis/shared';
import { api } from './lib/api';
import { AUTH_MODE, useAuth } from './lib/auth';
import { branding } from './lib/branding';
import { getTheme, setTheme, type Theme } from './lib/theme';
import { Alert, Button, Logo, Spinner } from './components/ui';
import { Avatar, Icon } from './components/pmis';
import { LoginPage, MustChangePasswordPage } from './pages/LoginPage';
import { ProfileDialog } from './pages/ProfileDialog';
import { AboutDialog } from './pages/AboutDialog';
import { AssistantDialog } from './components/AssistantDialog';
import { PrintFrame } from './components/PrintFrame';
import { DashboardPage } from './pages/DashboardPage';
import { ProjectsPage } from './pages/ProjectsPage';

const ProjectPage = lazy(() => import('./pages/project/ProjectPage'));
const NewProjectPage = lazy(() => import('./pages/NewProjectPage'));
const TimesheetPage = lazy(() => import('./pages/TimesheetPage'));
const ApprovalsPage = lazy(() => import('./pages/ApprovalsPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const MeetingsPage = lazy(() => import('./pages/MeetingsPage'));
const FastPage = lazy(() => import('./pages/FastPage'));
const MeetingPage = lazy(() => import('./pages/MeetingPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const CxPage = lazy(() => import('./pages/cx/CxPage'));
const AdminPage = lazy(() => import('./pages/admin/AdminPage'));
const ManualPage = lazy(() => import('./pages/ManualPage'));

export function App() {
  const { state } = useAuth();
  if (state.status === 'loading') return <div className="grid h-full place-items-center"><Spinner /></div>;
  if (state.status === 'anonymous') return <LoginPage notice={state.message} />;
  if (state.status === 'must-change') return <MustChangePasswordPage />;
  if (state.status === 'no-access') return <Centered title="Sem acesso ao PMIS" message={`${state.message} Peça ao administrador para dar um perfil ao seu usuário.`} />;
  if (state.status === 'error') return <Centered title="Não foi possível entrar" message={state.message} />;
  return <Shell />;
}

const MENU_KEY = 'pmis.menu';
/** Menu lateral recolhido (só os ícones), escolha de cada pessoa neste navegador. */
function useMenuCollapsed(): [boolean, (v: boolean) => void] {
  const [v, setV] = useState(() => { try { return localStorage.getItem(MENU_KEY) === 'recolhido'; } catch { return false; } });
  return [v, (next) => { setV(next); try { localStorage.setItem(MENU_KEY, next ? 'recolhido' : 'aberto'); } catch { /* sem armazenamento: vale só nesta visita */ } }];
}

function Shell() {
  const { me } = useAuth();
  const [mobileNav, setMobileNav] = useState(false);
  const [collapsed, setCollapsed] = useMenuCollapsed();
  const { pathname } = useLocation();
  useEffect(() => setMobileNav(false), [pathname]);
  if (!me) return null;
  const can = (p: string) => !!me.permissions[p];
  // Recolhido vale na tela grande; no celular o menu abre inteiro (por cima da tela).
  const mini = collapsed && !mobileNav;
  return (
    <div className="app-shell flex h-full">
      <aside className={clsx('no-print fixed inset-y-0 left-0 z-40 flex w-60 flex-col bg-side text-side-fg transition-[transform,width] lg:static lg:translate-x-0',
        mobileNav ? 'translate-x-0' : '-translate-x-full', mini && 'lg:w-16')}>
        <Link to="/" className={clsx('flex h-16 items-center gap-3', mini ? 'justify-center px-2' : 'px-5')} title={mini ? 'PMIS · Painel' : undefined}>
          {mini ? <img src={branding().logo.icon ?? '/brand/simbolo-ad.png'} alt="PMIS" className="size-8 object-contain" /> : <>
            <Logo onDark className="h-7" />
            <span className="border-l border-white/20 pl-3 text-sm font-semibold tracking-wide text-white">PMIS</span>
          </>}
        </Link>
        <div className={clsx('brand-gradient h-px opacity-70', mini ? 'mx-3' : 'mx-5')} />
        <nav className={clsx('flex-1 space-y-0.5 overflow-y-auto py-4 text-sm', mini ? 'px-2' : 'px-3')} aria-label="Menu principal">
          <NavItem mini={mini} to="/" icon="home" end>Painel</NavItem>
          <NavItem mini={mini} to="/projetos" icon="folder">Projetos</NavItem>
          {can('time.log') ? <NavItem mini={mini} to="/horas" icon="clock" end>Apontamento</NavItem> : null}
          {can('time.approve') ? <NavItem mini={mini} to="/horas/aprovacoes" icon="check">Aprovações</NavItem> : null}
          {can('resources.read') ? <NavItem mini={mini} to="/recursos" icon="users">Recursos</NavItem> : null}
          {can('meetings.read') ? <NavItem mini={mini} to="/reunioes" icon="meeting">Reuniões</NavItem> : null}
          {can('fast.read') ? <NavItem mini={mini} to="/canais-fast" icon="play">Canais FAST</NavItem> : null}
          {can('reports.read') || can('reports.financial') || can('reports.time') ? <NavItem mini={mini} to="/relatorios" icon="report">Relatórios</NavItem> : null}
          {can('cx.read') ? <NavItem mini={mini} to="/cx" icon="heart">Customer Experience</NavItem> : null}
          {can('admin.users') || Object.values(LIST_PERMISSIONS).some(can) || can('admin.settings') || can('admin.integrations') || can('admin.audit')
            ? <NavItem mini={mini} to="/admin" icon="settings">Administração</NavItem> : null}
        </nav>
        <button type="button" onClick={() => setCollapsed(!collapsed)} aria-expanded={!collapsed} title={collapsed ? 'Expandir o menu' : 'Recolher o menu (só os ícones)'}
          className={clsx('mx-3 mb-2 hidden items-center gap-2 rounded-lg px-3 py-2 text-xs text-side-fg/60 hover:bg-white/5 hover:text-white lg:flex', mini && 'justify-center px-0')}>
          <Icon name={collapsed ? 'chevron' : 'back'} className="size-4" />{mini ? <span className="sr-only">Expandir o menu</span> : 'Recolher o menu'}
        </button>
        {mini ? null : <div className="px-6 pb-4 text-[10px] text-white/40">PMIS ADD · versão {APP_VERSION_LABEL}</div>}
      </aside>
      {mobileNav ? <button type="button" aria-label="Fechar menu" className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setMobileNav(false)} /> : null}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onMenu={() => setMobileNav(true)} />
        <main className="min-h-0 flex-1 overflow-auto">
          <div className="mx-auto max-w-[1600px] px-4 py-5 sm:px-6 lg:px-8">
            <PrintFrame>
            <Suspense fallback={<Spinner />}>
              <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/projetos" element={<ProjectsPage />} />
                <Route path="/projetos/novo" element={<NewProjectPage />} />
                <Route path="/projetos/:id/*" element={<ProjectPage />} />
                <Route path="/horas" element={<TimesheetPage />} />
                <Route path="/horas/aprovacoes" element={<ApprovalsPage />} />
                <Route path="/recursos" element={<ResourcesPage />} />
                <Route path="/reunioes" element={<MeetingsPage />} />
                <Route path="/canais-fast" element={<FastPage />} />
                <Route path="/reunioes/:id" element={<MeetingPage />} />
                <Route path="/relatorios" element={<ReportsPage />} />
                <Route path="/cx/*" element={<CxPage />} />
                <Route path="/admin/*" element={<AdminPage />} />
                <Route path="/manual/*" element={<ManualPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
            </PrintFrame>
          </div>
        </main>
      </div>
    </div>
  );
}

/** Item do menu. `mini` (menu recolhido): só o ícone, com o nome ao passar o mouse e para o leitor de tela. */
function NavItem({ to, icon, children, end, mini }: { to: string; icon: string; children: string; end?: boolean; mini?: boolean }) {
  return (
    <NavLink to={to} end={end} title={mini ? children : undefined} aria-label={mini ? children : undefined}
      className={({ isActive }) => clsx('flex items-center gap-3 rounded-lg py-2 transition', mini ? 'justify-center px-0' : 'px-3',
        isActive ? 'bg-white/10 font-medium text-white shadow-inner' : 'text-side-fg/75 hover:bg-white/5 hover:text-white')}>
      {({ isActive }) => (<><Icon name={icon} className={clsx('size-[18px]', isActive && 'text-teal')} />{mini ? null : children}</>)}
    </NavLink>
  );
}

function TopBar({ onMenu }: { onMenu: () => void }) {
  const { me } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  return (
    <header className="no-print glass sticky top-0 z-20 flex h-16 shrink-0 items-center gap-3 border-b border-line px-4 sm:px-6">
      <button type="button" className="rounded-md p-2 text-muted hover:bg-panel-2 lg:hidden" onClick={onMenu} aria-label="Abrir menu"><Icon name="menu" /></button>
      <form className="relative max-w-md flex-1" onSubmit={(e) => { e.preventDefault(); nav(`/projetos?q=${encodeURIComponent(q)}&ativos=todos`); }} role="search">
        <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar projeto, cliente, OP, TAP ou pessoa…" aria-label="Buscar projetos"
          className="h-9 w-full rounded-full border border-line bg-panel-2/70 pl-9 pr-3 text-sm placeholder:text-muted/70 focus:bg-panel" />
      </form>
      <div className="ml-auto flex items-center gap-2">
        {AUTH_MODE === 'dev' ? <span className="rounded-full border border-warn/50 bg-warn-bg px-2 py-0.5 text-[11px] font-semibold text-warn" title="Login simulado: só para desenvolvimento">DEV</span> : null}
        <Notifications count={me?.unreadNotifications ?? 0} />
        {me ? <UserMenu /> : null}
      </div>
    </header>
  );
}

function Notifications({ count }: { count: number }) {
  const [open, setOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const nav = useNavigate();
  const { reloadMe } = useAuth();
  const { data, refetch } = useQuery({ queryKey: ['notifications'], queryFn: () => api<{ id: number; title: string; body: string | null; link: string | null; createdAt: string; readAt: string | null }[]>('/api/v1/me/notifications'), enabled: open });
  useOutside(ref, open, () => { setOpen(false); setConfirmClear(false); });
  const list = data ?? [];
  const unread = list.filter((n) => !n.readAt).length;
  // Cada ação: grava, relê a lista e o contador do sino.
  const act = async (path: string, method: 'POST' | 'DELETE', body?: unknown) => { await api(path, { method, body }); await refetch(); await reloadMe(); };
  const iconBtn = 'grid size-7 place-items-center rounded-md text-muted hover:bg-panel-2 hover:text-fg disabled:opacity-40';
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} className="relative rounded-full p-2 text-muted hover:bg-panel-2 hover:text-fg" aria-label={`Avisos (${count} não lidos)`}>
        <Icon name="bell" />
        {count ? <span className="absolute right-1 top-1 grid min-w-4 place-items-center rounded-full bg-err px-1 text-[10px] font-bold text-white">{count > 99 ? '99+' : count}</span> : null}
      </button>
      {open ? (
        <div role="region" aria-label="Lista de avisos" className="absolute right-0 top-full z-40 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-line bg-panel shadow-xl">
          <div className="flex items-center justify-between gap-2 border-b border-line py-1.5 pl-4 pr-2">
            <span className="text-sm font-semibold">Avisos</span>
            <div className="flex items-center gap-0.5">
              <button type="button" className={iconBtn} disabled={!unread} title="Marcar todos como lidos" aria-label="Marcar todos como lidos"
                onClick={() => void act('/api/v1/me/notifications/read', 'POST', {})}><Icon name="checks" className="size-[18px]" /></button>
              <button type="button" className={iconBtn} disabled={!list.length} title="Limpar todos os avisos" aria-label="Limpar todos os avisos"
                onClick={() => setConfirmClear(true)}><Icon name="clearAll" className="size-[18px]" /></button>
            </div>
          </div>
          {confirmClear ? (
            <div className="flex items-center justify-between gap-2 border-b border-line bg-warn-bg px-4 py-2 text-xs">
              <span>Excluir {list.length === 1 ? 'o aviso' : `os ${list.length} avisos`}? Não dá para desfazer.</span>
              <span className="flex shrink-0 gap-1">
                <button type="button" className="rounded-md px-2 py-1 hover:bg-panel-2" onClick={() => setConfirmClear(false)}>Cancelar</button>
                <button type="button" className="rounded-md bg-err px-2 py-1 font-medium text-white" onClick={() => { setConfirmClear(false); void act('/api/v1/me/notifications', 'DELETE'); }}>Limpar</button>
              </span>
            </div>
          ) : null}
          <ul className="max-h-96 overflow-auto">
            {list.length === 0 ? <li className="px-4 py-6 text-center text-sm text-muted">Nenhum aviso.</li> : null}
            {list.map((n) => (
              <li key={n.id} className={clsx('group flex items-start border-b border-line', !n.readAt && 'bg-accent/5')}>
                <button type="button" className="min-w-0 flex-1 px-4 py-2.5 text-left hover:bg-panel-2"
                  onClick={async () => { await api('/api/v1/me/notifications/read', { method: 'POST', body: { ids: [n.id] } }); setOpen(false); void reloadMe(); if (n.link) nav(n.link); }}>
                  <div className="flex items-center gap-1.5 text-sm font-medium">{!n.readAt ? <span className="size-1.5 shrink-0 rounded-full bg-accent" aria-label="Não lido" /> : null}{n.title}</div>
                  {n.body ? <div className="line-clamp-2 text-xs text-muted">{n.body}</div> : null}
                </button>
                <div className="flex shrink-0 flex-col gap-0.5 py-1.5 pr-2">
                  {!n.readAt ? <button type="button" className={iconBtn} title="Marcar como lido" aria-label={`Marcar como lido: ${n.title}`}
                    onClick={() => void act('/api/v1/me/notifications/read', 'POST', { ids: [n.id] })}><Icon name="check" className="size-4" /></button> : null}
                  <button type="button" className={clsx(iconBtn, 'hover:text-err')} title="Excluir este aviso" aria-label={`Excluir o aviso: ${n.title}`}
                    onClick={() => void act(`/api/v1/me/notifications/${n.id}`, 'DELETE')}><Icon name="trash" className="size-4" /></button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function useOutside(ref: React.RefObject<HTMLElement | null>, active: boolean, close: () => void) {
  useEffect(() => {
    if (!active) return;
    const out = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) close(); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', out);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', out); document.removeEventListener('keydown', esc); };
  }, [active, ref, close]);
}

/** Nome e foto no canto direito; o menu abre com o Assistente de IA, Perfil (com os avisos por e-mail), Manual, Sobre e Sair (e o tema). */
function UserMenu() {
  const { me, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(false);
  const [about, setAbout] = useState(false);
  const [assistant, setAssistant] = useState(false);
  const loc = useLocation();
  const [theme, setT] = useState<Theme>(getTheme());
  const ref = useRef<HTMLDivElement>(null);
  useOutside(ref, open, () => setOpen(false));
  if (!me) return null;
  const item = 'flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-panel-2';
  return (
    <div ref={ref} className="relative">
      <button type="button" data-testid="user-menu" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}
        className={clsx('flex items-center gap-2.5 rounded-full py-1 pl-1 pr-3 hover:bg-panel-2', open && 'bg-panel-2')}>
        <Avatar userId={me.id} name={me.displayName} version={me.photoVersion} size={34} />
        <span className="hidden min-w-0 text-left leading-tight sm:block">
          <span className="block max-w-48 truncate text-sm font-medium">{me.displayName}</span>
          <span className="block max-w-48 truncate text-[11px] text-muted">{me.jobTitle || me.roleNames.join(', ')}</span>
        </span>
        <Icon name="chevron" className={clsx('size-4 rotate-90 text-muted transition', open && '-rotate-90')} />
      </button>
      {open ? (
        <div role="menu" aria-label="Menu do usuário" className="absolute right-0 top-full z-40 mt-2 w-64 overflow-hidden rounded-xl border border-line bg-panel py-1 shadow-xl">
          <div className="flex items-center gap-3 border-b border-line px-4 py-3">
            <Avatar userId={me.id} name={me.displayName} version={me.photoVersion} size={40} />
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">{me.displayName}</div>
              <div className="truncate text-xs text-muted">{me.email}</div>
              <div className="truncate text-[11px] text-muted">{me.roleNames.join(', ')}</div>
            </div>
          </div>
          {me.permissions['ai.use'] ? <button type="button" role="menuitem" className={item} onClick={() => { setOpen(false); setAssistant(true); }}><Icon name="sparkles" className="size-4" />Assistente de IA</button> : null}
          <button type="button" role="menuitem" className={item} onClick={() => { setOpen(false); setProfile(true); }}><Icon name="user" className="size-4" />Meu perfil e avisos</button>
          <button type="button" role="menuitem" className={item} onClick={() => { setOpen(false); nav('/manual'); }}><Icon name="book" className="size-4" />Manual</button>
          <button type="button" role="menuitem" className={item} onClick={() => { setOpen(false); setAbout(true); }}><Icon name="info" className="size-4" />Sobre</button>
          <div className="flex items-center gap-1 border-y border-line px-4 py-2">
            <span className="mr-auto text-xs text-muted">Tema</span>
            {(['auto', 'light', 'dark'] as Theme[]).map((t) => (
              <button key={t} type="button" onClick={() => { setTheme(t); setT(t); }} aria-pressed={theme === t}
                className={clsx('rounded-md px-2 py-1 text-xs', theme === t ? 'bg-accent text-accent-fg' : 'text-muted hover:bg-panel-2')}>
                {t === 'auto' ? 'Auto' : t === 'light' ? 'Claro' : 'Escuro'}
              </button>
            ))}
          </div>
          <button type="button" role="menuitem" className={item} onClick={() => { setOpen(false); logout(); }}><Icon name="logout" className="size-4" />Sair</button>
        </div>
      ) : null}
      {profile ? <ProfileDialog onClose={() => setProfile(false)} /> : null}
      {about ? <AboutDialog onClose={() => setAbout(false)} /> : null}
      {assistant ? <AssistantDialog onClose={() => setAssistant(false)} screen={loc.pathname.replace(/\/\d+/g, '').replace(/^\//, '') || 'painel'} /> : null}
    </div>
  );
}

function Centered({ title, message }: { title: string; message: string }) {
  const { logout } = useAuth();
  return (
    <div className="grid h-full place-items-center p-4">
      <div className="w-full max-w-md space-y-3 rounded-xl border border-line bg-panel p-6">
        <Logo className="h-8" />
        <h1 className="text-lg font-semibold">{title}</h1>
        <Alert tone="err">{message}</Alert>
        <Button onClick={logout}>Voltar</Button>
      </div>
    </div>
  );
}
